import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiX, FiSave, FiCheck } from 'react-icons/fi';
import { BsFileSpreadsheet, BsGrid3X3Gap, BsBox } from 'react-icons/bs';
import { BiLoaderAlt } from 'react-icons/bi';
import { MdAttachMoney, MdInventory } from 'react-icons/md';
import { ChevronDown, Check } from 'lucide-react';
import { productAPI } from '../../api';
import toast from 'react-hot-toast';
import './Product.css';

export default function EditProductPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [parentProduct, setParentProduct] = useState<any>(null);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [initialForm, setInitialForm] = useState<any>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  const [form, setForm] = useState({
    sku: '',
    price: '',
    sellingPrice: '',
    discount: '',
    tax: '',
    hsn: '',
    gstAmount: '',
    netAmount: '',
    stock: '',
    warranty: '',
    replacementDays: '7',
    expiryDate: '',
    status: 'A',
    paymentOptions: ['Online'],
  });

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const res = await productAPI.getEditingProduct(id);
        const p = res.data.product;
        
        if (!p) throw new Error("Product not found");

        const loadedPrice = p.mrp || p.price || 0;
        const loadedSellingPrice = p.price || 0;
        let loadedDiscount = p.discount || 0;

        // Auto-correct discount if there's a discrepancy between saved prices and discount
        if (loadedPrice > 0 && loadedSellingPrice <= loadedPrice) {
          const calculatedDisc = ((loadedPrice - loadedSellingPrice) / loadedPrice) * 100;
          loadedDiscount = parseFloat(calculatedDisc.toFixed(2));
        } else if (loadedSellingPrice > loadedPrice) {
          loadedDiscount = 0;
        }

        const initialData = {
          sku: p.sku || '',
          price: String(loadedPrice || ''),
          sellingPrice: String(loadedSellingPrice || ''),
          discount: String(loadedDiscount || '0'),
          tax: p.tax !== undefined && p.tax !== null ? String(p.tax) : '',
          hsn: p.hsn || '',
          gstAmount: p.gstAmount ? String(p.gstAmount) : '',
          netAmount: p.netAmount ? String(p.netAmount) : '',
          stock: String(p.stock || ''),
          warranty: p.warranty || '',
          replacementDays: p.replacementDays?.toString() || '7',
          expiryDate: p.expiryDate ? new Date(p.expiryDate).toISOString().split('T')[0] : '',
          status: p.status || 'A',
          paymentOptions: Array.from(new Set([...(p.paymentOptions && Array.isArray(p.paymentOptions) ? p.paymentOptions : []), 'Online'])),
        };
        
        setForm(initialData);
        setInitialForm(initialData);
        
        setParentProduct(p);
      } catch (err) {
        toast.error('Failed to load product');
        console.error(err);
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      loadProduct();
    }
  }, [id]);
  const handleBlur = (e: any) => {
    const { name, value } = e.target;
    if (['price', 'sellingPrice'].includes(name) && value) {
      const parsed = parseFloat(Number(value).toFixed(2));
      setForm(prev => {
        const updated = { ...prev, [name]: String(parsed) };
        if (name === 'price') {
          const disc = Number(updated.discount) || 0;
          updated.sellingPrice = parsed > 0 ? String(parseFloat(Math.max(0, parsed - (parsed * disc / 100)).toFixed(2))) : '';
        } else if (name === 'sellingPrice') {
          const mrp = Number(updated.price) || 0;
          if (mrp > 0 && parsed <= mrp) {
            const calculatedDisc = ((mrp - parsed) / mrp) * 100;
            updated.discount = String(parseFloat(calculatedDisc.toFixed(2)));
          }
        }
        return updated;
      });
    }
  };

  const handleChange = (e: any) => {
    let { name, value } = e.target;
    
    // Restrict input to numbers only
    if (['price', 'sellingPrice', 'discount', 'tax', 'netAmount'].includes(name)) {
      value = value.replace(/[^0-9.]/g, '');
      if ((value.match(/\./g) || []).length > 1) return; // Prevent multiple dots
    } else if (name === 'hsn') {
      value = value.replace(/[^0-9]/g, '');
      if (value.length > 10) return;
    } else if (['stock', 'warranty', 'replacementDays'].includes(name)) {
      value = value.replace(/[^0-9]/g, '');
    }

    // Validation for max values
    if (name === 'discount' && value !== '' && value !== '.') {
      if (Number(value) > 100) return;
    }
    if (name === 'tax' && value !== '' && value !== '.') {
      if (Number(value) > 100) return;
    }
    if (name === 'warranty' && value !== '') {
      if (Number(value) > 150) return;
    }

    setForm(prev => {
      const updated = { ...prev, [name]: value };
      
      // Dynamic calculations
      const mrp = Number(updated.price) || 0;
      
      if (name === 'price') {
        const disc = Number(updated.discount) || 0;
        const calculatedSp = mrp > 0 ? parseFloat(Math.max(0, mrp - (mrp * disc / 100)).toFixed(2)) : 0;
        updated.sellingPrice = calculatedSp > 0 ? String(calculatedSp) : '';
      } else if (name === 'discount') {
        const disc = Number(value) || 0;
        const calculatedSp = mrp > 0 ? parseFloat(Math.max(0, mrp - (mrp * disc / 100)).toFixed(2)) : 0;
        updated.sellingPrice = calculatedSp > 0 ? String(calculatedSp) : '';
      } else if (name === 'sellingPrice') {
        const sp = Number(value) || 0;
        if (mrp > 0 && sp <= mrp) {
          const calculatedDisc = ((mrp - sp) / mrp) * 100;
          updated.discount = String(parseFloat(calculatedDisc.toFixed(2)));
        } else if (mrp > 0 && sp > mrp) {
           updated.discount = '0';
        }
      }
      
      if (['price', 'discount', 'sellingPrice', 'tax'].includes(name)) {
        const spToUse = Number(updated.sellingPrice) || 0;
        const taxToUse = Number(updated.tax) || 0;
        if (spToUse > 0 && updated.tax !== '') {
          const gstVal = parseFloat((spToUse * taxToUse / 100).toFixed(2));
          updated.gstAmount = String(gstVal);
          updated.netAmount = String(parseFloat((spToUse + gstVal).toFixed(2)));
        } else {
          updated.gstAmount = '';
          updated.netAmount = '';
        }
      }

      return updated;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const hasChanges = initialForm && JSON.stringify(initialForm) !== JSON.stringify(form);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    
    const newErrors: { [key: string]: string } = {};
    if (form.price === '') newErrors.price = 'Fill the MRP';
    if (form.sellingPrice === '') newErrors.sellingPrice = 'Fill the Selling Price';
    if (form.stock === '' || Number(form.stock) <= 0) newErrors.stock = 'Fill the Stock Quantity';

    if (form.hsn.length < 4 || form.hsn.length > 10) {
      newErrors.hsn = 'Fill the HSN Number';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('sku', form.sku);
      fd.append('mrp', String(Math.min(10000000, Math.max(0, Number(form.price) || 0))));
      fd.append('price', String(Math.min(10000000, Math.max(0, Number(form.sellingPrice) || 0))));
      fd.append('discount', String(Math.min(100, Math.max(0, Number(form.discount) || 0))));
      if (form.tax !== '') {
        fd.append('tax', form.tax);
      }
      if (form.hsn !== '') {
        fd.append('hsn', form.hsn);
      }
      if (form.gstAmount !== '') {
        fd.append('gstAmount', form.gstAmount);
      }
      if (form.netAmount !== '') {
        fd.append('netAmount', form.netAmount);
      }
      fd.append('stock', String(Math.max(0, Math.floor(Number(form.stock) || 0))));
      fd.append('warranty', form.warranty);
      fd.append('replacementDays', form.replacementDays);
      if (form.expiryDate !== undefined) fd.append('expiryDate', form.expiryDate);
      fd.append('status', form.status);
      fd.append('paymentOptions', JSON.stringify(form.paymentOptions));
      fd.append('product_id', id || '');
      await productAPI.update(fd);
      toast.success('Product updated successfully!');
      navigate('/products');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) return (
    <div style={{ textAlign: 'center', padding: '80px', color: '#94a3b8' }}>
      <div style={{ width: '36px', height: '36px', border: '3px solid #e2e8f0', borderTopColor: '#14b8a6', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
      Loading product details...
    </div>
  );

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <button
            onClick={() => navigate('/products')}
            className="back-pill-btn"
          >
            <FiArrowLeft size={16} style={{ display: 'inline-block' }} /> Back to Products
          </button>
          <h1 className="page-title" style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
            Edit Product
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            Modify product inventory, pricing, and reseller details below
          </p>
        </div>
      </div>

      {parentProduct && (
        <form onSubmit={handleSubmit} className="manual-form-container">
          {/* ── LEFT COLUMN: Form Fields ── */}
          <div className="manual-form-main">
            {/* Product Details (Read-Only) */}
            <div className="manual-card" style={{ borderLeft: '4px solid #6366f1' }}>
              <div className="manual-card-title">
                <BsGrid3X3Gap size={14} color="#6366f1" /> Selected Product Details (Read-Only)
              </div>
              <div style={{ marginBottom: '16px', marginTop: '4px' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Product Name</span>
                <strong style={{ fontSize: '15px', color: '#0f172a', display: 'block', marginTop: '2px' }}>{parentProduct.name}</strong>
              </div>
              <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</span>
                  <span style={{ fontSize: '14px', color: '#475569', fontWeight: '600', display: 'block', marginTop: '2px' }}>{parentProduct.category || 'N/A'}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Brand</span>
                  <span style={{ fontSize: '14px', color: '#475569', fontWeight: '600', display: 'block', marginTop: '2px' }}>{parentProduct.brand || 'N/A'}</span>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Parent SKU</span>
                  <span style={{ fontSize: '12px', fontFamily: 'monospace', background: '#e2e8f0', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontWeight: '700', display: 'inline-block', marginTop: '4px' }}>
                    {parentProduct.parentProduct?.sku || parentProduct.sku || 'N/A'}
                  </span>
                </div>
              </div>

              {parentProduct.description && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</span>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{parentProduct.description}</p>
                </div>
              )}

              {parentProduct.keySpecification && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Key Specifications</span>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{parentProduct.keySpecification}</p>
                </div>
              )}

            </div>

            {/* Pricing & SKU */}
            <div className="manual-card">
              <div className="manual-card-title">
                <MdAttachMoney size={15} color="#f59e0b" /> Pricing & Reseller SKU
              </div>
              <div className="manual-fields-grid">
                <div className="manual-field">
                  <label className="manual-field-label">Reseller SKU</label>
                  <input
                    name="sku"
                    value={form.sku}
                    onChange={handleChange}
                    placeholder="e.g. DS-2026-XYZ"
                    disabled
                  />
                </div>
                <div className="manual-field">
                  <label className="manual-field-label">MRP (₹) <span className="req">*</span></label>
                  <input
                    name="price"
                    type="number"
                    value={form.price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="0.00"
                    step="any"
                    min={0}
                    max={10000000}
                  />
                  {errors.price && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.price}</div>}
                </div>
                <div className="manual-field">
              <label className="manual-field-label">Selling Price (₹) <span className="req">*</span>  <div style={{ color: '#64748b', fontSize: '11px', fontStyle: 'italic', marginTop: '4px' }}>
                     (Inclusive of all taxes)
                  </div></label>
                  <input
                    name="sellingPrice"
                    type="number"
                    value={form.sellingPrice}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="0.00"
                    step="any"
                    min={0}
                    max={10000000}
                  />
                 
                  {errors.sellingPrice && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.sellingPrice}</div>}
                </div>
                <div className="manual-field">
                  <label className="manual-field-label">Discount Percent (%)</label>
                  <input
                    name="discount"
                    type="number"
                    value={form.discount}
                    onChange={handleChange}
                    placeholder="0"
                  />
                  {errors.discount && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.discount}</div>}
                </div>
                <div className="manual-field">
                  <label className="manual-field-label">GST Percent (%)</label>
                  <select
                    name="tax"
                    value={form.tax}
                    onChange={handleChange}
                  >
                    <option value="" disabled>Select GST %</option>
                    <option value="0">0%</option>
                    <option value="0.25">0.25%</option>
                    <option value="3">3%</option>
                    <option value="5">5%</option>
                    <option value="12">12%</option>
                    <option value="18">18%</option>
                    <option value="28">28%</option>
                  </select>
                </div>

                {form.sellingPrice && form.tax && (
                  <div className="manual-field">
                    <label className="manual-field-label">GST Amount (₹)</label>
                    <input
                      name="gstAmount"
                      type="text"
                      value={form.gstAmount}
                      disabled
                      style={{ background: '#f8fafc', color: '#64748b' }}
                    />
                  </div>
                )}

                <div className="manual-field">
                  <label className="manual-field-label">HSN Number</label>
                  <input
                    name="hsn"
                    type="number"
                    maxLength={10}
                    minLength={4}
                    value={form.hsn}
                    onChange={handleChange}
                    placeholder="Enter HSN number (digits between 4 and 10)"
                  />
                  {errors.hsn && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.hsn}</div>}
                </div>

                {form.sellingPrice && form.tax && (
                  <div className="manual-field">
                    <label className="manual-field-label">Net Amount (₹)</label>
                    <input
                      name="netAmount"
                      type="text"
                      value={form.netAmount}
                      disabled
                      style={{ background: '#f8fafc', color: '#64748b' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Inventory & Status */}
            <div className="manual-card" style={{ position: 'relative', zIndex: isStatusOpen ? 40 : 1 }}>
              <div className="manual-card-title">
                <MdInventory size={14} color="#10b981" /> Inventory & Status
              </div>
              <div className="manual-fields-grid">
                <div className="manual-field">
                  <label className="manual-field-label">Stock Quantity <span className="req">*</span></label>
                  <input
                    name="stock"
                    type="number"
                    value={form.stock}
                    onChange={handleChange}
                    placeholder="0"
                  />
                  {errors.stock && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.stock}</div>}
                </div>
                <div className="manual-field">
                  <label className="manual-field-label">Warranty (Months) e.g., 16 to 1year 4months</label>
                  <input
                    name="warranty"
                    type="number"
                    min={0}
                    max={150}
                    value={form.warranty}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    placeholder="Enter warranty duration (max 150 months)"
                  />
                  {errors.warranty && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.warranty}</div>}
                </div>
                <div className="manual-field">
                  <label className="manual-field-label">Replacement Days</label>
                  <select name="replacementDays" value={form.replacementDays} onChange={handleChange} style={{ width: '100%', padding: '11px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', color: '#0f172a', fontSize: '14px', fontWeight: '500', outline: 'none' }}>
                    <option value="0">0 (No Replacement)</option>
                    <option value="7">7 Days</option>
                    <option value="10">10 Days</option>
                    <option value="15">15 Days</option>
                    <option value="30">30 Days</option>
                  </select>
                </div>
                <div className="manual-field">
                  <label className="manual-field-label">
                    Expiry Date <span style={{ color: '#94a3b8', fontWeight: 'normal', fontSize: '11px', marginLeft: '4px' }}>(Optional)</span>
                  </label>
                  <input
                    name="expiryDate"
                    type="date"
                    value={form.expiryDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div className="manual-field" style={{ position: 'relative', zIndex: isStatusOpen ? 50 : 1 }}>
                  <label className="manual-field-label">Status</label>
                  <button 
                    type="button"
                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '11px 16px', background: 'white', border: '1px solid #e2e8f0',
                      borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s',
                      color: '#0f172a', fontSize: '14px', fontWeight: '500', width: '100%', outline: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ 
                        width: '8px', height: '8px', borderRadius: '50%', 
                        background: form.status === 'A' ? '#10b981' : form.status === 'D' ? '#eab308' : '#ef4444' 
                      }} />
                      {form.status === 'A' ? 'Active' : form.status === 'D' ? 'Draft' : 'Out of Stock'}
                    </div>
                    <ChevronDown size={16} strokeWidth={2.5} style={{ 
                      transform: isStatusOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
                      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                      color: '#94a3b8' 
                    }} />
                  </button>
                  
                  {isStatusOpen && (
                    <>
                      <div onClick={() => setIsStatusOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                      <div className="fade-in" style={{
                        position: 'absolute', top: 'calc(100% + 8px)', right: 0, left: 0,
                        background: 'white', borderRadius: '12px',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)',
                        border: '1px solid #f1f5f9', padding: '6px', zIndex: 50,
                      }}>
                        {['A', 'D', 'O'].map(s => {
                          const label = s === 'A' ? 'Active' : s === 'D' ? 'Draft' : 'Out of Stock';
                          const color = s === 'A' ? '#10b981' : s === 'D' ? '#eab308' : '#ef4444';
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() => { setForm(prev => ({ ...prev, status: s })); setIsStatusOpen(false); }}
                              style={{
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '10px 12px', borderRadius: '8px', border: 'none', background: 'transparent',
                                cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                                color: form.status === s ? '#14b8a6' : '#475569',
                                fontWeight: form.status === s ? '700' : '500',
                                fontSize: '14px'
                              }}
                              className="dropdown-item"
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
                                {label}
                              </div>
                              {form.status === s && <Check size={14} strokeWidth={3} />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="manual-card">
              <div className="manual-card-title" style={{ paddingBottom: '0' }}>
                 Payment Options
              </div>
              <p style={{fontSize:"12px", color:"#e7890dff"}}>Online payment is selected by default. You can continue with Cash on Delivery without selecting any payment option.</p>

              <div style={{ display: 'flex', gap: '12px', marginTop: '12px', paddingBottom: '16px', paddingLeft: '16px', flexWrap: 'wrap' }}>
                {['Online', 'Cash on Delivery'].map((option) => {
                  const isSelected = form.paymentOptions.includes(option);
                  const isOnline = option === 'Online';
                  return (
                    <div
                      key={option}
                      onClick={() => {
                        if (isOnline) return;
                        setForm(prev => {
                          const opts = new Set(prev.paymentOptions);
                          if (isSelected) opts.delete(option);
                          else opts.add(option);
                          return { ...prev, paymentOptions: Array.from(opts) };
                        });
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        borderRadius: '12px',
                        border: `1.5px solid ${isSelected ? '#10b981' : '#e2e8f0'}`,
                        background: isSelected ? (isOnline ? '#e2e8f0' : '#ecfdf5') : '#f8fafc',
                        cursor: isOnline ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: isSelected && !isOnline ? '0 4px 12px -2px rgba(16, 185, 129, 0.15)' : 'none',
                        transform: isSelected && !isOnline ? 'translateY(-1px)' : 'none',
                        opacity: isOnline ? 0.7 : 1
                      }}
                    >
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '6px',
                        background: isSelected ? (isOnline ? '#94a3b8' : '#10b981') : 'white',
                        border: `2px solid ${isSelected ? (isOnline ? '#94a3b8' : '#10b981') : '#cbd5e1'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}>
                        {isSelected && <FiCheck size={12} strokeWidth={4} color="white" />}
                      </div>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: isSelected ? '700' : '500',
                        color: isSelected ? (isOnline ? '#475569' : '#065f46') : '#475569',
                        transition: 'color 0.2s'
                      }}>
                        {option}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="manual-actions">
              <button type="button" className="manual-btn-cancel" onClick={() => navigate('/products')}>
                <FiX size={15} style={{ display: 'inline-block' }} /> Cancel
              </button>
              <button type="submit" className="manual-btn-save" disabled={loading || !hasChanges} style={{ opacity: (!hasChanges || loading) ? 0.5 : 1, cursor: (!hasChanges || loading) ? 'not-allowed' : 'pointer' }}>
                {loading ? (
                  <BiLoaderAlt size={15} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
                ) : (
                  <FiSave size={15} style={{ display: 'inline-block' }} />
                )}
                {loading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Interactive Live Preview ── */}
          <div className="manual-preview-sidebar">
            <div className="manual-preview-header">
              <div className="manual-preview-img-box" style={{ overflow: 'hidden', padding: parentProduct.images && parentProduct.images.length > 0 ? 0 : undefined }}>
                {parentProduct.images && parentProduct.images.length > 0 ? (
                  <img src={parentProduct.images[0]?.url || parentProduct.images[0]} alt={parentProduct.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <BsBox size={32} color="#94a3b8" />
                )}
              </div>
              <div className="manual-preview-name" title={parentProduct.name}>
                {parentProduct.name}
              </div>
              <div className="manual-preview-category">
                {parentProduct.category || 'No Category'}
              </div>
            </div>

            <div className="manual-preview-body">
              <div className="manual-preview-row">
                <span className="label">Brand</span>
                <span className="value">{parentProduct.brand || '—'}</span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Base SKU</span>
                <span className="value" style={{ fontFamily: 'monospace' }}>
                  {parentProduct.parentProduct?.sku || parentProduct.sku || '—'}
                </span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Custom SKU</span>
                <span className="value" style={{ fontFamily: 'monospace', color: '#4f46e5' }}>{form.sku || '—'}</span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Stock Available</span>
                <span className="value" style={{ color: form.stock ? '#10b981' : '#94a3b8' }}>
                  {form.stock ? `${form.stock} units` : 'Not specified'}
                </span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Listing Status</span>
                <span className="value">
                  <span style={{
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '700',
                    background: form.status === 'A' ? '#d1fae5' : form.status === 'D' ? '#fef9c3' : '#fee2e2',
                    color: form.status === 'A' ? '#065f46' : form.status === 'D' ? '#854d0e' : '#991b1b'
                  }}>
                    {form.status === 'A' ? 'ACTIVE' : form.status === 'D' ? 'DRAFT' : 'OUT OF STOCK'}
                  </span>
                </span>
              </div>

              {/* Dynamic Pricing breakdown widget */}
              <div className="manual-pricing-widget">
                <div className="manual-pricing-widget-header">Pricing Breakdown</div>
                <div className="manual-preview-row" style={{ padding: '6px 0' }}>
                  <span className="label">Retail Price</span>
                  <span className="value">₹{Number(form.price || 0).toFixed(0)}</span>
                </div>
                <div className="manual-preview-row" style={{ padding: '6px 0' }}>
                  <span className="label">Reseller Discount ({form.discount || 0}%)</span>
                  <span className="value" style={{ color: '#dc2626' }}>
                    -₹{(Number(form.price || 0) * Number(form.discount || 0) / 100).toFixed(0)}
                  </span>
                </div>
                <div className="manual-pricing-payout">
                  <span className="title">Net Customer Price</span>
                  <span className="value">
                    ₹{(Number(form.price || 0) * (1 - Number(form.discount || 0) / 100)).toFixed(0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
